import type { CallDeviceInfo } from '@dijitalapp/shared';

/*
 * What this device is, told to the other side of a call ("calling from a phone"): phone, tablet or computer, the
 * installed app or a browser tab, and the operating system. Only ever shown to people; best effort.
 */

interface UAData {
  mobile?: boolean;
  platform?: string;
}

type Os = NonNullable<CallDeviceInfo['os']>;

export interface DeviceSignals {
  ua: string;
  uaData?: UAData | undefined;
  maxTouchPoints: number;
  coarse: boolean;
  hover: boolean;
  /** The screen's shorter side in CSS pixels. */
  shortSide: number;
  standalone: boolean;
}

function osOf(s: DeviceSignals): Os | undefined {
  const p = (s.uaData?.platform ?? '').toLowerCase();
  if (p === 'android') return 'android';
  if (p === 'ios') return 'ios';
  if (p === 'windows') return 'windows';
  if (p === 'macos') return 'macos';
  if (p === 'chrome os' || p === 'chromeos') return 'chromeos';
  if (p === 'linux') return 'linux';
  const ua = s.ua;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  // iPadOS asks for the desktop site: a "Mac" with a touch screen.
  if (/Macintosh/.test(ua) && s.maxTouchPoints > 1) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/CrOS/.test(ua)) return 'chromeos';
  if (/Windows/.test(ua)) return 'windows';
  if (/Mac OS X|Macintosh/.test(ua)) return 'macos';
  if (/Linux|X11/.test(ua)) return 'linux';
  return undefined;
}

function kindOf(s: DeviceSignals, os: Os | undefined): NonNullable<CallDeviceInfo['kind']> {
  const ua = s.ua;
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && s.maxTouchPoints > 1)) return 'tablet';
  if (/iPhone|iPod/.test(ua)) return 'phone';
  if (s.uaData?.mobile === true) return 'phone';
  if (os === 'android') return /Mobile/.test(ua) ? 'phone' : 'tablet';
  // A touch screen without a mouse: by the screen's size.
  if (s.coarse && !s.hover) return s.shortSide < 600 ? 'phone' : 'tablet';
  return 'desktop';
}

/** The device from these signals (pure, for tests). */
export function deviceFrom(s: DeviceSignals): CallDeviceInfo {
  const os = osOf(s);
  return { kind: kindOf(s, os), app: s.standalone ? 'pwa' : 'browser', ...(os ? { os } : {}) };
}

let cached: CallDeviceInfo | null = null;

/** This device (worked out once; the display mode does not change while the app runs). */
export function thisDevice(): CallDeviceInfo {
  if (cached) return cached;
  try {
    const mq = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
    const nav = navigator as Navigator & { userAgentData?: UAData; standalone?: boolean };
    cached = deviceFrom({
      ua: nav.userAgent,
      uaData: nav.userAgentData,
      maxTouchPoints: nav.maxTouchPoints ?? 0,
      coarse: mq('(pointer: coarse)'),
      hover: mq('(hover: hover)'),
      shortSide: Math.min(window.screen?.width ?? 0, window.screen?.height ?? 0) || Math.min(window.innerWidth, window.innerHeight),
      standalone: nav.standalone === true || mq('(display-mode: standalone)') || mq('(display-mode: fullscreen)') || mq('(display-mode: minimal-ui)') || mq('(display-mode: window-controls-overlay)'),
    });
  } catch {
    cached = {};
  }
  return cached;
}
